import { type IconProps, SearchOutlineIcon } from '@carboncopyco/icons';
import {
  Button,
  type DropdownCapsuleState,
  type DropdownFilterItemContent,
  FilterCapsule,
  type FilterCommonProps,
  InputText,
  LoadingDots,
  MultiDropdownFilterItem,
  Paper,
  type RenderItem,
  TextBase,
} from '@carboncopyco/ui';
import { removeUndefinedValues } from '@carboncopyco/utils';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { type MouseEvent, useEffect, useMemo, useState } from 'react';
import type { RequireAtLeastOne } from 'type-fest';
import { useDebounce } from 'use-debounce';
import { InfiniteList } from '../InfiniteList';
import { SearchDropdownSkeleton } from './SearchDropdownSkeleton';

export type SearchDropdownProps = RequireAtLeastOne<
  {
    fixedItems?: DropdownFilterItemContent<string>[];
    searchableItems?: DropdownFilterItemContent<string>[];
  },
  'fixedItems' | 'searchableItems'
> &
  FilterCommonProps & {
    onClick?: (event: MouseEvent) => void;
    leftIcon?: React.FC<IconProps>;
    selectedItems: Record<string, string>;
    query?: string;
    setQuery?: (query: string) => void;
    applyButton?: React.ReactNode;
    onChange: (item: string) => void;
    renderItem?: RenderItem<string>;
    isLoading: boolean;
    isLoadingBatch: boolean;
    onInputChange?: (value: string) => void;
    fetchNextBatch: () => void;
  } & (
    | {
        onClear?: () => void;
        footer: React.ReactElement;
      }
    | {
        onClear: () => void;
        footer?: never;
      }
  );

export const SearchDropdown = ({
  capsuleWidth,
  contentWidth,
  size,
  rounded,
  leftIcon,
  placeholder,
  onClick,
  disabled,
  selectedItems,
  searchableItems,
  fixedItems,
  query = '',
  setQuery,
  onClear,
  footer,
  applyButton,
  renderItem,
  onChange,
  isLoading,
  isLoadingBatch,
  onInputChange,
  fetchNextBatch,
}: SearchDropdownProps) => {
  const [capsuleState, setCapsuleState] =
    useState<DropdownCapsuleState>('default');

  useEffect(() => {
    if (disabled) {
      setCapsuleState('disabled');
      return;
    }
    setCapsuleState('default');
  }, [disabled]);

  const [searchQuery] = useDebounce(query, 500);

  useEffect(() => {
    if (onInputChange) {
      onInputChange(searchQuery);
    }
  }, [searchQuery]);

  const firstSelectedItem = useMemo(
    () =>
      Object.keys(removeUndefinedValues(selectedItems)).length
        ? Object.values(selectedItems)[0]
        : undefined,
    [selectedItems]
  );

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger disabled={disabled}>
        <FilterCapsule
          width={capsuleWidth}
          size={size}
          rounded={rounded}
          leftIcon={leftIcon}
          capsuleState={capsuleState}
          placeholder={placeholder}
          numberOfSelected={
            Object.keys(removeUndefinedValues(selectedItems)).length
          }
          label={firstSelectedItem}
          onClick={onClick}
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-100"
          align="start"
          sideOffset={4}
          loop
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          <Paper
            radius
            shadow="md"
          >
            <div
              style={{
                width: contentWidth ? `${contentWidth}px` : 'initial',
              }}
              className="flex flex-col max-h-[360px] overflow-hidden"
            >
              {fixedItems && (
                <InfiniteList
                  isLoading={isLoading}
                  isLoadingBatch={isLoadingBatch}
                  onReachBottom={fetchNextBatch}
                  loader={<SearchDropdownSkeleton rows={7} />}
                  batchLoader={
                    <div className="flex grow justify-center items-center">
                      <LoadingDots size={10} />
                    </div>
                  }
                  loaderPlaceholderSpace={20}
                >
                  <div className="p-1 gap-1">
                    {fixedItems.map((item) => (
                      <MultiDropdownFilterItem
                        key={item.id}
                        item={item}
                        onChange={() => {
                          onChange(item.id);
                        }}
                        checked={!!selectedItems[item.id]}
                        renderItem={renderItem}
                      />
                    ))}
                  </div>
                </InfiniteList>
              )}
              {searchableItems && setQuery && (
                <div className="flex flex-1 flex-col overflow-y-auto">
                  <div className="flex flex-col flex-initial p-1 overflow-hidden">
                    <div className="pb-1">
                      <InputText
                        leftElement={<SearchOutlineIcon color="#9B9AA2" />}
                        value={query}
                        placeholder="Search"
                        onChange={(event) => {
                          setQuery(event.target.value);
                        }}
                      />
                    </div>
                    <div className="flex flex-col gap-1 flex-initial overflow-y-auto">
                      {searchQuery && !searchableItems.length && !isLoading ? (
                        <div className="flex grow items-center justify-center py-2">
                          <TextBase
                            label={`No Results for "`}
                            color="text-ds-natural-600"
                          />
                          <div className="max-w-min w-20 truncate text-ds-natural-600">
                            <TextBase
                              label={searchQuery}
                              color="text-ds-natural-600"
                              ellipsis
                            />
                          </div>
                          <TextBase
                            label={`"`}
                            color="text-ds-natural-600"
                          />
                        </div>
                      ) : (
                        <InfiniteList
                          isLoading={isLoading}
                          isLoadingBatch={isLoadingBatch}
                          onReachBottom={fetchNextBatch}
                          loader={<SearchDropdownSkeleton rows={7} />}
                          batchLoader={
                            <div className="flex grow justify-center items-center">
                              <LoadingDots size={10} />
                            </div>
                          }
                          loaderPlaceholderSpace={20}
                        >
                          {searchableItems.map((item) => (
                            <MultiDropdownFilterItem
                              key={item.id}
                              item={item}
                              onChange={() => {
                                onChange(item.id);
                              }}
                              checked={!!selectedItems[item.id]}
                              renderItem={renderItem}
                            />
                          ))}
                        </InfiniteList>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {footer || (
                <div className="flex justify-between p-4 [&>button]:p-0 ">
                  <Button
                    link
                    blue
                    label="Clear"
                    onClick={onClear}
                  />
                  {applyButton}
                </div>
              )}
            </div>
          </Paper>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};
